<template>
  <PDPMainContainer>
    <loader
      v-if="forceRedirectOnClientSide"
      class="my-5"
    />
    <div v-else>
      <MobileDealBannerHeader
        v-if="showBannerDetails"
        :banner-details="deal.banner_details"
        class="d-lg-none"
      />
      <div class="container pdp-section">
        <!-- if taxonomy, show breadcrumbs -->
        <Breadcrumbs
          v-if="showBreadcrumbs"
          :deal-name="deal.public_name"
          :group="breadcrumbObjects.find((c) => c.slug === 'group')"
          :category="breadcrumbObjects.find((c) => c.slug === 'category')"
          :subcategory="breadcrumbObjects.find((c) => c.slug === 'subcategory')"
        />

        <PDPDealStory
          :deal="deal"
          :user="user"
        />
      </div>
      <PDPSectionTabs
        enable-sticky
        :deal="deal"
        :has-founders-post="hasFoundersPost"
      />
      <div
        v-if="!deal.is_info && inactiveIndefinitely"
        class="container pdp-section"
      >
        <EmbeddedSmartCollection
          collection-slug="also-bought-with"
          :template-slug="deal.slug"
          :relative-to-container="true"
        />
        <!-- show Plans & Features section (DealTermsAndFeatures + PricingTable) for sold out -->
        <DealTermsAndFeatures
          v-if="isSoldOut"
          class="mt-5"
          :deal="deal"
        />
        <PricingTable
          v-if="isSoldOut"
          :deal="deal"
        />
      </div>
      <ProductsFounders
        v-if="hasFoundersPost"
        class="pdp-section"
        :product-url="deal.product_url"
        :is-select="!deal.is_marketplace"
        :founders="deal.founders"
      />
      <div class="container">
        <div class="row">
          <div class="col-lg-7 col-xl-8">
            <PDPDiscussionTabs
              :deal="deal"
              :user="user"
              :filter-comments="filterComments"
            />
          </div>
        </div>
      </div>

      <SignUpBanner v-if="!user.isAuthenticated" />
    </div>
  </PDPMainContainer>
</template>

<script>
import mapKeys from 'lodash/mapKeys';
import get from 'lodash/get';
import catchify from 'catchify';
import dayjs from 'dayjs';
import PDPDealStory from '../../../components/pdp/PDPDealStory';
import PDPSectionTabs from '../../../components/pdp/PDPSectionTabs';
import PDPDiscussionTabs from '../../../components/pdp/discussion/PDPDiscussionTabs';
import PDPMainContainer from '../../../components/pdp/PDPMainContainer';
import { getProductPageLDSchemaJSONString, getTaxonomyBreadcrumbLDSchemaJSONString, getVideoObjectLDSchemaJSONString } from '../../../util/schemaLD';
import EmbeddedSmartCollection from '../../../components/smartcollections/EmbeddedSmartCollection';
import Breadcrumbs from '../../../components/pdp/Breadcrumbs';
import MobileDealBannerHeader from '../../../components/pdp/MobileDealBannerHeader.vue';
import { getPdpData } from '../../../util/get-pdp-data';
import { DiscussionType } from '../../../constants/discussions';
import { ACTIVITY_TYPE, PDP_BREADCRUMB_SLUGS, SUMO_DAY_BANNER_TEXT } from '../../../constants/global';
import WishlistsMixin from '~/mixins/wishlists-mixin';
import PrivyMixin from '~/mixins/add-privy';
import Loader from '../../../components/common/Loader.vue';
import SignUpBanner from '../../../components/common/SignUpBanner.vue';
import DealTermsAndFeatures from '~/components/products/terms-and-features/DealTermsAndFeatures.vue';
import PricingTable from '~/components/products/pricing-table/PricingTable.vue';
import ProductsFounders from '~/components/products/founders/ProductsFounders.vue';

const DISCUSSION_PERMALINK_REGEX = /^#([rq])([0-9]*)$/;

export default {
  components: {
    Breadcrumbs,
    EmbeddedSmartCollection,
    Loader,
    PDPDealStory,
    PDPMainContainer,
    PDPSectionTabs,
    PDPDiscussionTabs,
    MobileDealBannerHeader,
    SignUpBanner,
    DealTermsAndFeatures,
    PricingTable,
    ProductsFounders,
  },
  mixins: [PrivyMixin, WishlistsMixin],
  provide() {
    // to ease the use for all descendents let's provide the deal, story, and user to all
    // ~ descendents so that we do not need to thread these down through the stack.
    // note: this was added WAY after the pdp page had been built, so not used as much as it could.
    const { deal, story, user } = this;

    return { deal, story, user };
  },
  layout: 'base',
  DiscussionType,
  async asyncData(context) {
    const data = await getPdpData(context);
    // if we failed to load the details for the deal and user from the server then redirect to
    // ~ browse and hopefully they will have more luck there.
    // JIC, should already be redirected by redirect middleware
    if (!data) {
      // note: Should we do something better here. 404 or toast, etc?
      return context.redirect('/');
    }

    const { store } = context;
    const { stats } = data;

    await store.dispatch('wishlists/setWishlistCount', { value: stats?.wishlists_count || 0 });

    const filterComments = context.$config.allowFilterComments === 'enabled' || false;

    return {
      // the deal, story and user will all come from the data.
      ...data,
      forceRedirectOnClientSide: false,
      filterComments,
    };
  },
  computed: {
    group() {
      const group = this.breadcrumbObjects.find((c) => c.slug === 'group');

      if (group && group.values.length > 0) {
        return mapKeys(group.values[0], (v, k) => k.replace('slug', 'value'));
      }

      return null;
    },
    category() {
      const category = this.breadcrumbObjects.find((c) => c.slug === 'category');

      if (category && category.values.length > 0) {
        return mapKeys(category.values[0], (v, k) => k.replace('slug', 'value'));
      }

      return null;
    },
    subcategory() {
      const subcategory = this.breadcrumbObjects.find((c) => c.slug === 'subcategory');

      if (subcategory && subcategory.values.length > 0) {
        return mapKeys(subcategory.values[0], (v, k) => k.replace('slug', 'value'));
      }

      return null;
    },
    breadcrumbObjects() {
      return this.deal?.attributes?.filter((a) => PDP_BREADCRUMB_SLUGS.includes(a.slug));
    },
    showBreadcrumbs() {
      // only for staff initially
      return this.breadcrumbObjects.length === 3;
    },
    isSoldOut() {
      // notes: info deals will show as 'ended'
      //       'sold out'/is_ended is a subset of is_inactive_indefinitely
      return this.deal.buy_button.is_ended;
    },
    gaTrackingId() {
      if (!get(this, 'deal.partner_ga_tags')) {
        return null;
      }

      // partner_ga_tags is an array of GA tracking IDs, in the future we might need to store
      // multiple tags for the same deal, for now we just need the most recent one.
      const gaTags = [...this.deal.partner_ga_tags].reverse();
      return gaTags[0];
    },
    linkHash() {
      return this.$route?.hash || '';
    },
    wishlistsCount() {
      return this.$store.getters['wishlists/wishlistsCount'];
    },
    inactiveIndefinitely() {
      return this.deal.stage_details.is_inactive_indefinitely;
    },
    hasFoundersPost() {
      return !!this.deal.founders?.posts?.length;
    },
    currentlyBeforeNextDealStage() {
      const nextStageStartDate = this.deal?.stage_details?.next_stage_start_date;
      if (!nextStageStartDate) {
        return false;
      }

      const dayjsNow = dayjs();
      const diffInMilliseconds = dayjs(nextStageStartDate).diff(dayjsNow);

      return diffInMilliseconds > 0;
    },
    lowCodesRemaining() {
      return this.deal.shows_codes_remaining && this.deal.codes_remaining > 0;
    },
    showBannerDetails() {
      if (!this.deal.banner_details?.text) {
        return false;
      }

      return this.currentlyBeforeNextDealStage
          || this.lowCodesRemaining
          || this.deal.banner_details?.text === SUMO_DAY_BANNER_TEXT;
    },
  },
  async created() {
    // Update cookies, needed as server can't update from asyncData; this is
    // used for routing to either nuxt or django in nginx
    if (this.deal.render_nuxt_pdp.nuxt) {
      this.$cookies.set('pdp_type', 'nuxt');
    } else {
      this.$cookies.set('pdp_type', 'django');
    }

    // If the deal and user settings specify, we will redirect to Django
    if (!this.deal.render_nuxt_pdp.nuxt) {
      this.forceRedirectOnClientSide = true;
    } else {
      this.setEcommerceDataLayer();
    }

    if (typeof window === 'undefined') {
      return;
    }

    if (this.forceRedirectOnClientSide) {
      // ensure we keep all params except rm, which we will be forcing to d below
      const queryParams = Object.entries(this.$route.query)
        .filter(([k]) => k !== 'rm')
        .map(([k, v]) => `${k}=${v}`)
        .join('&');

      let redirectUrl = `${this.$route.path}`;
      if (queryParams) {
        redirectUrl += `?${queryParams}`;
      }
      redirectUrl += this.$route.hash;

      // eslint-disable-next-line no-restricted-globals
      location.href = redirectUrl;

      return;
    }

    await this.$store.dispatch('wishlists/setWishlistCount', { value: this.stats?.wishlists_count || 0 });

    // unfortunately the URL fragment is not available in asyncData, so we are forced to do this
    // ~ discussion permalink checking here in created.
    if (this.deal.render_nuxt_pdp.support_discussion_pages && this.$route?.hash) {
      // use a regex to validate and extract what we care about from the URL fragment
      const discussionFragment = this.$route.hash.match(DISCUSSION_PERMALINK_REGEX);
      if (discussionFragment) {
        const pathType = discussionFragment[1] === 'r'
          ? 'reviews'
          : 'questions';

        // Since we are redirecting the user let's show a spinner here.
        this.forceRedirectOnClientSide = true;

        // We are burying discussion id in the "slug" with a -{id} suffix, but the
        // ~ prefix slug part is entirely optional, so we can just specify the provided
        // ~ id and call it a day. See discussion-page-mixin.js DISCUSSION_SLUG_REGEX
        // eslint-disable-next-line no-restricted-globals
        location.href = `${this.deal.get_absolute_url}${pathType}/${discussionFragment[2]}/`;
        return;
      }
    }

    // If staying on PDP log activity
    this.postPageActivity();
  },
  mounted() {
    if (this.gaTrackingId) {
      // If partner wants to track, let's register their google analytics ID
      // note: This config immediately starts the tracking process.
      this.gtag('js', new Date());
      this.gtag('config', this.gaTrackingId); // 'send_page_view' defaults to true
    }
  },
  methods: {
    gtag(...args) {
      // https://developers.google.com/tag-platform/gtagjs/install#google-analytics
      window.dataLayer = window.dataLayer || [];
      dataLayer.push(args);
    },
    async postPageActivity() {
      const { $axios, $cookies, deal } = this;

      // User must be authenticated to register activity, but not views
      const options = {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': $cookies.get('csrftoken'),
        },
      };

      const data = { activity_event: ACTIVITY_TYPE.DEAL_VIEW };

      await catchify($axios({
        method: 'post',
        url: `/deal/${deal.id}/activity/`,
        data,
        ...options,
      }));
    },
    setEcommerceDataLayer() {
      const product = {
        name: this.deal.public_name,
        category: this.deal.is_marketplace ? 'marketplace' : 'select',
        sku: this.deal.id.toString(),
        price: this.deal.default_price.toString(),
        url: `https://appsumo.com${this.deal.get_absolute_url}`,
      };

      if (this.deal.media_url) {
        product.image = `${this.deal.media_url}?width=250&height=250`;
      }

      // we want to include information about the detail as its own layer
      this.$gtm.push({
        contentGroup1: this.deal.listing_type,
        contentGroup2: this.deal.deal_status.toLowerCase(),
      });

      this.$gtm.push({
        event: 'productDetails',
        ecommerce: {
          detail: {
            products: [product],
          },
        },
      });
    },
  },
  head() {
    const { deal, story } = this;

    if (!deal || !story) {
      return { title: 'AppSumo' };
    }

    let title = deal.public_name;

    if (story.meta_title) {
      title += ` - ${story.meta_title} | AppSumo`;
    } else {
      title += ' | AppSumo';
    }

    const optionalScripts = [];

    if (deal.top_5_reviews.length
      && deal.deal_review.review_count
      && deal.deal_review.average_rating) {
      // we tried using a skip: for this, but we were getting null pointer issues because
      // ~ getProductPageLDSchemaJSONString() was still being called
      optionalScripts.push({
        hid: 'pdp-schema',
        type: 'application/ld+json',
        innerHTML: getProductPageLDSchemaJSONString(deal),
      });
    }

    if (this.group && this.deal) {
      optionalScripts.push({
        hid: 'bc-schema',
        type: 'application/ld+json',
        innerHTML: getTaxonomyBreadcrumbLDSchemaJSONString({
          group: this.group,
          category: this.category,
          subcategory: this.subcategory,
          deal: this.deal,
        }),
      });
    }

    if (this.story.videos?.length) {
      const videoSchema = getVideoObjectLDSchemaJSONString(this.deal);

      if (videoSchema) {
        optionalScripts.push({
          hid: 'video-schema',
          type: 'application/ld+json',
          innerHTML: getVideoObjectLDSchemaJSONString(this.deal),
        });
      }
    }

    return {
      title,
      meta: [
        {
          hid: 'og:title',
          property: 'og:title',
          content: title,
        },
        {
          hid: 'description',
          property: 'description',
          content: story.card_description,
          skip: !story.card_description,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: story.card_description,
          skip: !story.card_description,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: deal.media_url,
          skip: !deal.media_url,
        },
        {
          hid: 'og:type',
          property: 'og:type',
          content: 'article',
        },
        {
          hid: 'twitter:title',
          property: 'twitter:title',
          content: title,
        },
        {
          hid: 'twitter:description',
          property: 'twitter:description',
          content: story.card_description,
          skip: !story.card_description,
        },
        {
          hid: 'twitter:image',
          property: 'twitter:image',
          content: deal.media_url,
          skip: !deal.media_url,
        },
      ],
      link: [
        {
          hid: 'canonical',
          rel: 'canonical',
          href: `https://appsumo.com${deal.get_absolute_url}`,
        },
      ],
      script: [
        {
          async: true,
          src: `https://www.googletagmanager.com/gtag/js?id=${this.gaTrackingId}`,
          skip: !this.gaTrackingId,
        },
        ...optionalScripts,
      ],
      // avoid encoding (&quot; instead ")
      __dangerouslyDisableSanitizersByTagID: {
        'pdp-schema': ['innerHTML'],
        'bc-schema': ['innerHTML'],
        'video-schema': ['innerHTML'],
      },
    };
  },
};
</script>

<style>
  .pdp-section {
    margin-bottom: 40px;
  }

  /* let default text size be 16px */
  p,
  .fs-16 {
    font-size: 16px;
  }

  iframe {
    width: 100%;
    height: 100%;
    aspect-ratio: 16/9;
  }

  @media (min-width: 1024px) {
    .pdp-section {
      margin-bottom: 64px;
    }
  }
</style>
